import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import { confirmable, createConfirmation } from 'react-confirm';
import isArray from "lodash/isArray";
import isFunction from "lodash/isFunction";
import BasicButton from '../../../../components/BasicButton';
import BasicButtonGroup from '../../../../components/BasicButtonGroup';
import Popup from '../../../../components/Popup';
import ERROR_FIELD from "../../../../constants/error-fields";
import UxHelpers from '../../../../helpers/ux-helpers';

const ExtendedConfirmDialog = props => {
  const {
    show,
    proceed,
    dismiss,
    cancel,
    params = {},
    bodyRender,
    buttonsRender,
    textSuccessBtn = 'Ok',
    textCancelBtn = 'Cancel',
    modeless,
    dismissWhenClose,
    hasCloseButton,
    popupStyles,
  } = props;

  const handleError = (response, setErrors) => {
    const {
      data: { fields },
    } = response;
    if (fields && typeof setErrors === 'function') {
      const obj = {};
      for (let [key, value] of Object.entries(fields)) {
        const error = isArray(value) ? value[0] : value;
        obj[key] = ERROR_FIELD[error] || `${error}`;
      }
      setErrors(obj);
    } else {
      cancel(response);
    }
  };

  const proceedHandler = data => {
    proceed(data);
    UxHelpers.overflowLock('unlock');
  };

  const cancelHandler = () => {
    cancel();
    UxHelpers.overflowLock('unlock');
  };

  const dismissHandler = () => {
    dismiss();
    UxHelpers.overflowLock('unlock');
  };

  const btnGroupStyles = {
    justifyContent: 'center',
    width: '100%',
  };

  const buttons = buttonsRender ? buttonsRender(
      {
        proceed: proceedHandler,
        dismiss: dismissHandler,
        cancel: cancelHandler,
        dismissWhenClose,
        textSuccessBtn,
        textCancelBtn,
        btnGroupStyles
      }
  ) :(
    <BasicButtonGroup
      groupStyles={btnGroupStyles}
    >
      <BasicButton
        controlFunc={proceedHandler}
        btnWidth={200}
        btnHeight={40}
        btnFontWeight={500}
        btnArrow={false}
        btnTextColor="#27282d"
        btnText={textSuccessBtn}
      />
      <BasicButton
        controlFunc={dismissWhenClose ? dismissHandler : cancelHandler}
        btnWidth={200}
        btnHeight={40}
        btnFontWeight={500}
        btnArrow={false}
        btnTextColor="#27282d"
        btnText={textCancelBtn}
      />
    </BasicButtonGroup>
  );

  const body = useMemo(() => {
    const formActions = {
      dismiss: dismissHandler,
      proceed: proceedHandler,
      cancel: cancelHandler,
      handleError,
    };

    if (bodyRender && isFunction(bodyRender)) {
      return bodyRender(
          formActions,
          params ? { ...params, dismissWhenClose } : { dismissWhenClose }
      );
    } else {
      return '';
    }
  }, [bodyRender, cancelHandler, dismissHandler, dismissWhenClose, handleError, params, proceedHandler]);

  return (
    show && (
      <Popup
        actions={{
          dismiss,
          cancel,
        }}
        buttonsRender={buttons}
        popupStyles={popupStyles}
        bodyRender={body}
        modeless={modeless}
        dismissWhenClose={dismissWhenClose}
        hasCloseButton={hasCloseButton}
      />
    )
  );
};

ExtendedConfirmDialog.propTypes = {
  bodyRender: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
    PropTypes.string,
    PropTypes.func,
  ]),
  buttonsRender: PropTypes.func,
  modeless: PropTypes.bool,
  dismissWhenClose: PropTypes.bool,
  textSuccessBtn: PropTypes.string,
  textCancelBtn: PropTypes.string,
};

export default createConfirmation(confirmable(ExtendedConfirmDialog));
